import React, { useState } from 'react';
import { Heart, Loader2, Gift } from 'lucide-react';
import { api } from '@/lib/api';
import useSEO from '@/hooks/useSEO';

const PRESETS = [100, 300, 500, 1000, 4500];

export default function Support() {
    useSEO({
        title: 'Donate to Linguistic Quest — help keep the puzzles running',
        description: 'Linguistic Quest is not yet self-sustaining. If you love our language puzzles for children but cannot subscribe, please consider donating £1 or more.',
        path: '/support',
    });
    const [amount, setAmount] = useState(300);
    const [custom, setCustom] = useState('');
    const [loading, setLoading] = useState(null); // 'card' | 'paypal' | null
    const [err, setErr] = useState(null);

    const effectiveCents = custom !== ''
        ? Math.round(parseFloat(custom || '0') * 100)
        : amount;

    const validate = () => {
        setErr(null);
        if (!effectiveCents || effectiveCents < 100) { setErr('Minimum is £1.'); return false; }
        if (effectiveCents > 300000) { setErr('Maximum is £3,000 — thank you though!'); return false; }
        return true;
    };

    const start = async () => {
        if (!validate()) return;
        setLoading('card');
        try {
            const { data } = await api.post('/support/checkout', {
                amount_cents: effectiveCents, origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not start checkout, please try again.');
            setLoading(null);
        }
    };

    const startPaypal = async () => {
        if (!validate()) return;
        setLoading('paypal');
        try {
            const { data } = await api.post('/support/paypal/checkout', {
                amount_cents: effectiveCents, origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Could not start PayPal checkout, please try again.');
            setLoading(null);
        }
    };

    return (
        <main className="max-w-2xl mx-auto px-6 py-16" data-testid="page-support">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-lq-peach-100 text-lq-peach-600">
                <Heart size={22} />
            </div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold text-lq-ink-black">Donate to Linguistic Quest</h1>
            <p className="mt-4 text-lq-ink-700 leading-relaxed">
                We&rsquo;ll be honest with you: <strong>Linguistic Quest has not yet reached the critical mass it needs to be
                self-sustaining.</strong> Building native-speaker audio, new languages and family-safe puzzles costs real money,
                and right now subscriptions alone don&rsquo;t cover it.
            </p>
            <p className="mt-3 text-lq-ink-700 leading-relaxed">
                If you like what we&rsquo;re building but you&rsquo;re not in a position to subscribe, we kindly ask you to
                consider <strong>any donation — &pound;1 or more</strong>. Every pound goes directly into keeping the puzzles
                running for children everywhere.
            </p>

            <div className="mt-5 rounded-2xl border border-lq-purple-300 bg-lq-purple-100/50 p-4 flex items-start gap-3" data-testid="support-gift-perk">
                <Gift size={20} className="mt-0.5 shrink-0 text-lq-purple-700" />
                <p className="text-sm text-lq-ink-900">
                    <strong>Donate &pound;45 or more and pay it forward:</strong> you can gift a full year of paid access
                    to anyone you choose. After your donation, simply enter their email address and we&rsquo;ll deliver
                    the gift for you.
                </p>
            </div>

            <div className="mt-6 bg-white border border-lq-ink-100 rounded-3xl p-6">
                <p className="text-sm font-semibold text-lq-ink-900">Choose an amount</p>
                <div className="mt-3 grid grid-cols-5 gap-2">
                    {PRESETS.map((p) => (
                        <button key={p}
                            onClick={() => { setAmount(p); setCustom(''); }}
                            className={`py-2.5 rounded-xl font-semibold border-2 transition-colors ${custom === '' && amount === p ? 'border-lq-purple-500 bg-lq-purple-100 text-lq-purple-700' : 'border-lq-ink-100 text-lq-ink-700 hover:border-lq-purple-300'}`}
                            data-testid={`support-preset-${p}`}>
                            &pound;{p / 100}{p === 4500 && <span className="block text-[9px] font-normal leading-tight">+ gift a year</span>}
                        </button>
                    ))}
                </div>
                <div className="mt-3 flex items-center gap-2">
                    <span className="text-lq-ink-700 font-semibold">&pound;</span>
                    <input
                        type="number" min="1" max="3000" step="0.5" placeholder="Or enter your own amount"
                        value={custom}
                        onChange={(e) => setCustom(e.target.value)}
                        className="flex-1 border border-lq-ink-100 rounded-xl px-4 py-2.5 focus:outline-none focus:border-lq-purple-500"
                        data-testid="support-custom-amount"
                    />
                </div>
                {err && <p className="mt-3 text-sm text-lq-error" data-testid="support-error">{err}</p>}
                <button onClick={start} disabled={!!loading}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold py-3 rounded-full disabled:opacity-60"
                    data-testid="support-checkout-btn">
                    {loading === 'card' ? <><Loader2 size={16} className="animate-spin"/> Opening secure checkout&hellip;</> : <><Heart size={16}/> Donate {effectiveCents >= 100 ? `£${(effectiveCents / 100).toFixed(2).replace(/\.00$/, '')}` : ''} by card</>}
                </button>
                <button onClick={startPaypal} disabled={!!loading}
                    className="mt-2.5 w-full inline-flex items-center justify-center gap-2 bg-[#ffc439] hover:bg-[#f2ba36] text-[#003087] font-bold py-3 rounded-full disabled:opacity-60"
                    data-testid="support-paypal-btn">
                    {loading === 'paypal' ? <><Loader2 size={16} className="animate-spin"/> Opening PayPal&hellip;</> : <>Donate {effectiveCents >= 100 ? `£${(effectiveCents / 100).toFixed(2).replace(/\.00$/, '')}` : ''} with <span className="italic font-black">PayPal</span></>}
                </button>
                <p className="mt-3 text-[11px] text-center text-lq-ink-500">Card checkout by Stripe (Apple&nbsp;Pay / Google&nbsp;Pay, no account needed) &middot; or your PayPal account</p>
            </div>

            <p className="mt-5 text-xs text-lq-ink-500">
                This is a voluntary supporter payment to Southern African Traders Ltd (trading as Linguistic Quest) and is not a
                charitable donation. Donations of &pound;45+ include one gift of a 12-month Explorer plan for an email address of your choice.
            </p>
            <p className="mt-3 text-sm text-lq-ink-500">
                Prefer something with puzzles attached? A <a href="/pricing" className="text-lq-purple-700 hover:underline">subscription from &pound;3.49/month</a> supports us too &mdash; and unlocks everything for your family.
            </p>
        </main>
    );
}
